import React from 'react';
import { Box, UnstyledButton } from '@mantine/core';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { ProductInfo } from '../compareData';
import { GRAY, INK } from '../constants';
import styles from './ProductCard.module.scss';

interface ProductCardProps {
  product: ProductInfo;
  /** Currently shown render (swatch-selected for S3, otherwise the default image). */
  image: string;
  /** Present only for products with collar-color swatches (S3). */
  onSelectColor?: (image: string) => void;
}

/** Product column: spotlight thumbnail (+ optional color selector), name, tagline, description. */
export const ProductCard: React.FC<ProductCardProps> = ({ product, image, onSelectColor }) => {
  const { t } = useTranslation();
  const hasSwatches = Boolean(product.swatches);
  return (
    <Box className={styles.card}>
      <Box className={classNames(styles.spotlight, { [styles.spotlightWithSwatches]: hasSwatches })}>
        <img src={image} alt={t(product.nameKey)} />
        {product.swatches && (
          <Box className={styles.swatchRow}>
            <Box className={styles.swatches}>
              {product.swatches.map((s) => (
                <UnstyledButton
                  key={s.image}
                  type="button"
                  aria-label={t('compare_v2_select_color')}
                  onClick={() => onSelectColor?.(s.image)}
                  className={classNames(styles.swatch, { [styles.swatchSelected]: image === s.image })}
                  style={{ background: s.color }}
                />
              ))}
            </Box>
          </Box>
        )}
      </Box>
      <Box fw={600} lts="-0.2px" className={styles.name}>
        {t(product.nameKey)}
      </Box>
      <Box fw={700} c={INK} lts="-0.5px" className={styles.tagline}>
        {t(product.taglineKey)}
      </Box>
      <Box c={GRAY} className={styles.description}>
        {t(product.descKey)}
      </Box>
    </Box>
  );
};
